import { CalendarCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { GRAVUN_BRAND } from "@/branding";
import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { addDays, formatTime } from "./calendarModel";

interface PublicPage {
  slug: string;
  name: string;
  description: string | null;
  duration_minutes: number;
  timezone: string;
}

interface Confirmation {
  appointment_id: string;
  starts_at: string;
  ends_at: string;
  page_name: string;
}

const inputClass =
  "h-11 w-full rounded-[10px] border border-input bg-card px-3 text-[14px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

/**
 * Public, unauthenticated booking page (/#/book/:slug). Matches the Gravun
 * auth-surface design: navy brand panel + white booking card. All reads and
 * the booking itself go through anon-granted SECURITY DEFINER RPCs.
 */
export const PublicBookingPage = () => {
  const { slug = "" } = useParams();
  const [page, setPage] = useState<PublicPage | null | "missing">(null);
  const [date, setDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d;
  });
  const [slots, setSlots] = useState<string[]>([]);
  const [slot, setSlot] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);

  useEffect(() => {
    (async () => {
      const { data } = await getSupabaseClient().rpc("get_booking_page", {
        p_slug: slug,
      });
      setPage(data && data.length > 0 ? (data[0] as PublicPage) : "missing");
    })();
  }, [slug]);

  const dateKey = useMemo(() => date.toISOString().slice(0, 10), [date]);

  useEffect(() => {
    if (!page || page === "missing") return;
    (async () => {
      setSlots([]);
      setSlot(null);
      const { data } = await getSupabaseClient().rpc("get_booking_slots", {
        p_slug: slug,
        p_date: dateKey,
      });
      setSlots(
        ((data ?? []) as { slot_start: string }[]).map((s) => s.slot_start),
      );
    })();
  }, [page, slug, dateKey]);

  const book = async () => {
    if (!slot) return;
    setSubmitting(true);
    setError(null);
    const { data, error: rpcError } = await getSupabaseClient().rpc(
      "book_appointment",
      {
        p_slug: slug,
        p_name: name,
        p_email: email,
        p_phone: phone || null,
        p_starts_at: slot,
      },
    );
    setSubmitting(false);
    if (rpcError) {
      setError(rpcError.message);
      return;
    }
    setConfirmation((data as Confirmation[])[0]);
  };

  return (
    <div className="flex min-h-screen">
      <div className="relative hidden flex-col justify-between bg-[oklch(0.22_0.055_262)] p-12 text-white lg:flex lg:w-[38%]">
        <img
          className="h-9 w-auto self-start"
          src={GRAVUN_BRAND.wordmarkWhite}
          alt={GRAVUN_BRAND.name}
        />
        <div className="max-w-sm space-y-3">
          <h2 className="text-3xl font-bold leading-tight tracking-tight">
            Book a time that works for you.
          </h2>
          <p className="text-base text-white/60">
            Pick a slot and we'll confirm your appointment instantly.
          </p>
        </div>
        <p className="text-sm text-white/40">
          © {new Date().getFullYear()} {GRAVUN_BRAND.name} Inc.
        </p>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center bg-background p-4 lg:p-10">
        <div className="w-full max-w-xl">
          {page === null ? null : page === "missing" ? (
            <div className="rounded-2xl border border-border-subtle bg-card p-8 text-center">
              <h1 className="text-xl font-semibold">
                This booking page is unavailable
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                The link may be wrong or the page has been turned off.
              </p>
            </div>
          ) : confirmation ? (
            <div className="rounded-2xl border border-border-subtle bg-card p-8 text-center">
              <CalendarCheck className="mx-auto size-10 text-success" />
              <h1 className="mt-3 text-2xl font-semibold tracking-tight">
                You're booked!
              </h1>
              <p className="mt-2 text-[15px] text-foreground">
                {confirmation.page_name}
              </p>
              <p className="text-[14px] text-muted-foreground">
                {new Date(confirmation.starts_at).toLocaleDateString(
                  undefined,
                  {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  },
                )}{" "}
                · {formatTime(confirmation.starts_at)},{" "}
                {formatTime(confirmation.ends_at)}
              </p>
              <p className="mt-3 text-[13px] text-muted-foreground">
                Your appointment is saved under {email}.
              </p>
            </div>
          ) : (
            <div className="rounded-2xl border border-border-subtle bg-card p-8">
              <img
                className="mb-6 h-7 w-auto lg:hidden dark:hidden"
                src={GRAVUN_BRAND.wordmarkNavy}
                alt={GRAVUN_BRAND.name}
              />
              <h1 className="text-2xl font-semibold tracking-tight">
                {page.name}
              </h1>
              {page.description ? (
                <p className="mt-1 text-sm text-muted-foreground">
                  {page.description}
                </p>
              ) : null}
              <p className="mt-1 text-[13px] text-muted-foreground">
                {page.duration_minutes} minutes
              </p>

              <div className="mt-5 flex items-center justify-between">
                <Button
                  variant="outline"
                  size="icon"
                  className="size-8"
                  onClick={() => setDate((d) => addDays(d, -1))}
                >
                  <ChevronLeft className="size-4" />
                </Button>
                <span className="text-[14.5px] font-medium">
                  {date.toLocaleDateString(undefined, {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="size-8"
                  onClick={() => setDate((d) => addDays(d, 1))}
                >
                  <ChevronRight className="size-4" />
                </Button>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-4">
                {slots.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSlot(s)}
                    className={cn(
                      "h-10 rounded-[10px] border text-[13.5px] font-medium transition-colors",
                      slot === s
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-card text-foreground hover:border-primary/50",
                    )}
                  >
                    {formatTime(s)}
                  </button>
                ))}
                {slots.length === 0 ? (
                  <p className="col-span-full py-4 text-center text-[13.5px] text-muted-foreground">
                    No openings on this day, try another date.
                  </p>
                ) : null}
              </div>

              {slot ? (
                <div className="mt-5 flex flex-col gap-3 border-t border-border-subtle pt-5">
                  <input
                    className={inputClass}
                    placeholder="Full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                  <input
                    className={inputClass}
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <input
                    className={inputClass}
                    type="tel"
                    placeholder="Phone (optional)"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                  {error ? (
                    <p className="text-[13px] text-destructive">{error}</p>
                  ) : null}
                  <Button
                    size="lg"
                    className="w-full"
                    disabled={submitting || !name.trim() || !email.trim()}
                    onClick={book}
                  >
                    {submitting
                      ? "Booking…"
                      : `Confirm ${formatTime(slot)} appointment`}
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

PublicBookingPage.path = "/book/:slug";
